import React, { useState } from 'react';
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MentionText } from '@/components/mentions/MentionText';
import { slicePreservingMentions } from '@/components/mentions/mention-state';
import type { GeneralPostData } from '../../types';

interface GeneralPostContentProps {
  post: GeneralPostData;
}

const CHARACTER_LIMIT = 300;

export function GeneralPostContent({ post }: GeneralPostContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const content = post.generalContent || '';
  const isLongContent = content.length > CHARACTER_LIMIT;
  const displayedContent = isExpanded ? content : slicePreservingMentions(content, CHARACTER_LIMIT);

  return (
    <div className="space-y-4">
      <div>
        <p className="whitespace-pre-wrap text-sm text-foreground/90">
          <MentionText text={displayedContent} />
          {isLongContent && !isExpanded && '...'}
        </p>
        {isLongContent && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-auto p-0 text-primary hover:text-primary hover:bg-transparent"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </Button>
        )}
      </div>
      {post.generalImages && post.generalImages.length > 0 && (
        <div
          className={`grid gap-2 ${post.generalImages.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
        >
          {post.generalImages.map((img: string, idx: number) => (
            <img
              key={idx}
              src={img}
              alt={`Post attachment ${idx + 1}`}
              className="rounded-lg object-cover w-full h-full max-h-[400px]"
            />
          ))}
        </div>
      )}
      {post.generalLocation && (
        <div className="flex items-center text-xs text-muted-foreground mt-2">
          <MapPin className="w-3 h-3 mr-1" />
          {post.generalLocation}
        </div>
      )}
    </div>
  );
}
